import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { DateInput, FORM_CONTROL_CLASS, FORM_LABEL_CLASS, Modal } from '@safetyhub/ui';
import {
  formatDateToLocal,
  formatObligationInterval,
  isProcessSubjectKey,
  RESULT_CODES_REQUIRING_CONFIRMATION,
  type LegalObligationRow,
  type RecordLegalObligationResultResponse,
} from '@safetyhub/shared';
import { apiErrorMessage } from '../../lib/api';
import { recordLegalObligationResult } from '../../lib/legalObligationEvidence';
import { todayDateInputInTimezone } from '../../lib/localDate';
import { EditModalFooter } from '../common/EditModalFooter';
import { fulfilledAtToIso } from './EvidenceModal';

// 「사업장의 사실을 사람이 확인하는 결과 코드」는 shared `RESULT_CODES_REQUIRING_CONFIRMATION` 한 벌이다 — 서버 400 판정과 같은 집합
// (2026-11-03 S9 — 종전엔 손사본이 있었다).

export interface ResultRecordModalProps {
  companyId: string;
  siteId: string;
  row: LegalObligationRow;
  /**
   * 결과의 대상 키 — 공정 × 유해인자 대상(작업환경측정)이면 그 쌍의 키, 현장 단위 의무(특수건강진단 등)면 `''`.
   * 서버(`resolveResultSubjectKey`)가 의무의 대상 축과 맞는지 다시 판정한다
   */
  subjectKey: string;
  /** 본문 첫 줄의 굵은 이름 — 측정 대상이면 「공정 · 유해인자」, 현장 단위면 의무 이름 */
  subjectLabel: string;
  /** 현장 유효 시간대 — 측정일 변환과 「오늘」 */
  timezone: string;
  onClose: () => void;
  onRecorded: (result: RecordLegalObligationResultResponse) => void | Promise<void>;
}

/**
 * 측정·진단 결과 기록(E7 · 설계 2차 §6 끝 「결과 코드는 사람이 고른다」) — 결과표는 기계가 읽지 않으니 사람이 결과 분류·실시일·(측정이면 선택)
 * 노출기준 대비 비율을 옮긴다. 선택지는 그 의무의 결과 변형(`RESULT_FACT`)의 코드 — 어휘의 정본은 카탈로그 변형 줄이다.
 *
 * 호스트가 둘이다 — 작업환경측정 대상 쌍은 현장 사실 명부(`SiteLegalFactsPanel`)에서, 현장 단위 결과 의무(특수건강진단 6/12/24개월 —
 * `SHORTEN_NEXT`)는 의무 목록 행(`SiteObligationsPage`)에서 연다. 갈리는 것은 문구와 비율 칸뿐이다(비율은 노출기준이 있는 측정에만).
 */
export default function ResultRecordModal({
  companyId,
  siteId,
  row,
  subjectKey,
  subjectLabel,
  timezone,
  onClose,
  onRecorded,
}: ResultRecordModalProps) {
  const isMeasurement = isProcessSubjectKey(subjectKey);
  const choices = row.intervalVariants
    .filter((v) => v.selector === 'RESULT_FACT')
    .flatMap((v) => v.valueKeys.map((code) => ({ code, label: v.label ?? code, interval: v.interval })));
  const today = todayDateInputInTimezone(timezone);
  const [occurredOn, setOccurredOn] = useState(today);
  const [code, setCode] = useState('');
  const [ratio, setRatio] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const needsConfirmation = RESULT_CODES_REQUIRING_CONFIRMATION.has(code);
  const ratioValue = !isMeasurement || ratio.trim() === '' ? null : Number(ratio);
  const dateLabel = isMeasurement ? '측정일' : '실시일';
  const mutation = useMutation({
    mutationFn: () =>
      recordLegalObligationResult(companyId, siteId, row.obligationId, {
        occurredAt: fulfilledAtToIso(occurredOn, timezone),
        resultCode: code,
        subjectKey,
        ...(ratioValue !== null ? { exposureRatio: ratioValue } : {}),
        ...(needsConfirmation ? { confirmed } : {}),
      }),
    onSuccess: onRecorded,
    onError: (err) =>
      setErrorMessage(apiErrorMessage(err) ?? `${isMeasurement ? '측정 결과' : '결과'}를 기록하지 못했습니다.`),
  });
  const blockedReason = !occurredOn
    ? `${dateLabel}을 선택하세요.`
    : !code
      ? '결과를 고르세요.'
      : ratioValue !== null && (!Number.isFinite(ratioValue) || ratioValue < 0)
        ? '노출기준 대비 비율은 0 이상의 수여야 합니다.'
        : needsConfirmation && !confirmed
          ? '변화 없음 확인이 필요합니다.'
          : '';
  const isDirty = code !== '' || ratio !== '' || occurredOn !== today;

  return (
    <Modal
      open
      onClose={onClose}
      title={isMeasurement ? '측정 결과 기록' : '결과 기록'}
      size="md"
      closeLocked={mutation.isPending}
      isDirty={isDirty}
      footer={
        <EditModalFooter
          isDirty={isDirty}
          blockedReason={blockedReason}
          submitLabel="결과 기록"
          submitDisabled={!!blockedReason || mutation.isPending}
          busy={mutation.isPending}
          busyLabel="기록 중..."
          onSubmit={() => {
            if (blockedReason || mutation.isPending) return;
            setErrorMessage(null);
            mutation.mutate();
          }}
          onFallbackClose={onClose}
        />
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-ink-700">
          <span className="font-bold text-ink-900">{subjectLabel}</span>
          {isMeasurement
            ? '의 작업환경측정 결과를 기록합니다. 결과표의 값을 옮겨 적으세요.'
            : '의 결과를 기록합니다. 결과표의 판정을 옮겨 적으세요 — 고른 결과가 다음 주기의 간격을 바꿉니다.'}
        </p>
        <div>
          <label htmlFor="legal-result-occurred-on" className={FORM_LABEL_CLASS}>
            {dateLabel}
          </label>
          <DateInput
            id="legal-result-occurred-on"
            label={dateLabel}
            value={occurredOn}
            onChange={setOccurredOn}
            max={today}
            disabled={mutation.isPending}
            className="w-full"
          />
        </div>
        <div>
          <label htmlFor="legal-result-code" className={FORM_LABEL_CLASS}>
            결과
          </label>
          <select
            id="legal-result-code"
            className={FORM_CONTROL_CLASS}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            disabled={mutation.isPending}
          >
            <option value="">결과 고르기…</option>
            {choices.map((c) => (
              <option key={c.code} value={c.code}>
                {c.label} — 다음 {isMeasurement ? '측정' : '주기'} {formatObligationInterval(c.interval)} 이내
              </option>
            ))}
          </select>
        </div>
        {isMeasurement && (
          <div>
            <label htmlFor="legal-result-ratio" className={FORM_LABEL_CLASS}>
              노출기준 대비 비율 (선택 · 측정치 ÷ 노출기준)
            </label>
            <input
              id="legal-result-ratio"
              type="number"
              inputMode="decimal"
              min={0}
              step="0.0001"
              className={FORM_CONTROL_CLASS}
              value={ratio}
              onChange={(e) => setRatio(e.target.value)}
              disabled={mutation.isPending}
              placeholder="예: 0.42"
            />
          </div>
        )}
        {needsConfirmation && (
          <label className="flex items-start gap-2 text-sm text-ink-800">
            <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} disabled={mutation.isPending} />
            <span>
              최근 1년간 공정 설비·작업방법·설비 이전·사용 화학물질 변경 등 측정 결과에 영향을 주는 변화가 없었음을 확인합니다(시행규칙
              제190조②). 「2회 연속 노출기준 미만」은 직전 결과로 확인합니다.
            </span>
          </label>
        )}
        {errorMessage && (
          <p role="alert" className="rounded-[10px] border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {errorMessage}
          </p>
        )}
      </div>
    </Modal>
  );
}

/**
 * 결과 기록 뒤 토스트 — 두 호스트가 같은 문구를 쓴다. 소급 입력이 뒤쪽 「2회 연속 미만」의 전제를 깨면 서버가 경고만 싣는다(거부 아님 · D5 선례)
 * — 숨기지 않는다. 노출기준 초과 → 개선 증명·계획 제출 사건이 새로 생겼으면 함께 알린다(시행규칙 제188조③ · 서버 `followUpEvent`)
 */
export function describeResultRecorded(result: RecordLegalObligationResultResponse): {
  message: string;
  tone: 'success' | 'warning';
} {
  const followUp = result.followUpEvent?.created
    ? ' 노출기준 초과라 「개선 증명·계획 제출」(60일 이내) 사건이 등록됐습니다.'
    : '';
  // 응답의 대상 키가 말한다 — 공정 × 유해인자 대상이면 측정 결과, 현장 단위면 결과(특수건강진단 등)
  const noun = isProcessSubjectKey(result.subjectKey) ? '측정 결과' : '결과';
  if (result.laterConsecutiveBelowBrokenAt) {
    return {
      message: `${noun}를 기록했습니다. ${formatDateToLocal(new Date(result.laterConsecutiveBelowBrokenAt))} 결과의 「2회 연속 미만」은 이 결과 때문에 더 이상 성립하지 않습니다 — 그 결과를 확인하세요.${followUp}`,
      tone: 'warning',
    };
  }
  return { message: `${noun}를 기록했습니다. 다음 주기가 그 결과로 다시 계산됩니다.${followUp}`, tone: 'success' };
}
